/**
 * Pasek "Paragony Renoma (S02)" (ANEKS 6) - obok PasekWz.
 * Miesiace z list PDF od Renomy (GET /kontrola-faktur/paragony/lista), brak w systemie
 * i bez XML (z podsumowanie.liczby: paragony_brak / paragony_bez_pliku; zapas: razem z listy),
 * przycisk "Wgraj listę PDF" (POST multipart `plik`) z wynikiem inline i odswiezeniem strony.
 * Klik w "brak w systemie" ustawia filtr 'paragony'.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { kontrolaFakturService } from '../../services/kontrolaFakturService';
import { formatujMiesiac, formatujKwote, formatujDateCzas, KOLOR_PARAGON } from './format';

const n = (v) => (v === null || v === undefined ? '—' : v);

/** 'luty 2026' -> 'luty'; rok tylko gdy lista obejmuje wiecej niz jeden rok. */
const nazwaMiesiaca = (miesiac, wieleLat) => {
  const pelna = formatujMiesiac(miesiac);
  return wieleLat ? pelna : pelna.replace(/\s\d{4}$/, '');
};

const PasekParagonow = ({ liczby, aktywnyFiltr, onFiltr, onOdswiez }) => {
  const [dane, setDane] = useState(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState(null);
  const [wgrywanie, setWgrywanie] = useState(false);
  const [wynik, setWynik] = useState(null);
  const [bladWgrania, setBladWgrania] = useState(null);
  const inputRef = useRef(null);

  const laduj = useCallback(async () => {
    setLadowanie(true);
    try {
      const d = await kontrolaFakturService.paragonyLista();
      setDane(d && typeof d === 'object' ? d : { miesiace: [], razem: {} });
      setBlad(null);
    } catch (err) {
      if (!err?.isAuthError) setBlad(err?.message || 'Nie udało się pobrać listy paragonów');
    } finally {
      setLadowanie(false);
    }
  }, []);

  useEffect(() => { laduj(); }, [laduj]);

  const wgraj = async (plik) => {
    if (!plik) return;
    setWgrywanie(true);
    setWynik(null);
    setBladWgrania(null);
    try {
      const w = await kontrolaFakturService.paragonyListaWgraj(plik);
      setWynik({ ...(w || {}), plik_nazwa: plik.name });
      await laduj();
      if (onOdswiez) onOdswiez(w);
    } catch (err) {
      if (!err?.isAuthError) setBladWgrania(err?.data?.komunikat || err?.message || 'Nie udało się wczytać listy paragonów');
    } finally {
      setWgrywanie(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const miesiace = Array.isArray(dane?.miesiace) ? dane.miesiace : [];
  const razem = dane?.razem || {};
  const l = liczby || {};
  const brak = l.paragony_brak ?? razem.brak;
  const bezXml = l.paragony_bez_pliku
    ?? (razem.na_liscie !== undefined && razem.z_xml !== undefined ? razem.na_liscie - razem.z_xml : undefined);
  const wieleLat = new Set(miesiace.map(m => String(m.miesiac || '').slice(0, 4))).size > 1;
  const aktywny = aktywnyFiltr === 'paragony';
  const wynikMiesiace = wynik?.miesiace && typeof wynik.miesiace === 'object' ? Object.entries(wynik.miesiace) : [];

  return (
    <div data-testid="pasek-paragonow" style={{
      marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
      backgroundColor: 'var(--bg)', borderLeft: `3px solid ${KOLOR_PARAGON}`,
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px' }}>
        <span style={{ fontWeight: 600, color: KOLOR_PARAGON }}>Paragony Renoma (S02)</span>

        {ladowanie && !dane && <span style={{ color: 'var(--text-muted)' }}>Ładowanie…</span>}
        {blad && <span data-testid="paragony-blad" style={{ color: 'var(--danger)' }}>{blad}</span>}

        {!blad && dane && miesiace.length === 0 && (
          <span data-testid="paragony-pusto" style={{ color: 'var(--text-muted)' }}>Nie wgrano jeszcze listy paragonów od Renomy</span>
        )}
        {miesiace.map(m => (
          <span key={m.miesiac} data-testid="paragony-miesiac" style={{ whiteSpace: 'nowrap' }}
            title={[
              formatujMiesiac(m.miesiac),
              `na liście ${n(m.na_liscie)} · w systemie ${n(m.w_systemie)} · brak ${n(m.brak)} · z XML ${n(m.z_xml)}`,
              m.plik_nazwa ? `plik: ${m.plik_nazwa}` : null,
              m.wgrano ? `wgrano: ${formatujDateCzas(m.wgrano)}` : null,
            ].filter(Boolean).join('\n')}>
            <span style={{ color: 'var(--text-secondary)' }}>{nazwaMiesiaca(m.miesiac, wieleLat)}</span>{' '}
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{n(m.na_liscie)}</span>
            {Number(m.brak) > 0 && <span style={{ color: 'var(--danger)' }}> (brak {m.brak})</span>}
          </span>
        ))}

        {(miesiace.length > 0 || brak !== undefined) && (
          <button
            type="button"
            data-testid="paragony-brak"
            aria-pressed={aktywny}
            onClick={() => onFiltr && onFiltr('paragony')}
            title="Pokaż paragony Renomy (najpierw brakujące w systemie)"
            style={{
              padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '10px', cursor: 'pointer',
              border: `1px solid ${KOLOR_PARAGON}`,
              background: aktywny ? KOLOR_PARAGON : 'var(--card-bg)',
              color: aktywny ? '#fff' : KOLOR_PARAGON,
            }}
          >
            brak w systemie (w zakresie dat): {n(brak)}
          </button>
        )}
        {bezXml !== undefined && (
          <span data-testid="paragony-bez-xml" style={{ whiteSpace: 'nowrap' }} title="Paragony z listy Renomy, do których nie przyszedł jeszcze XML mailem">
            <span style={{ fontWeight: 700, color: Number(bezXml) > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>{n(bezXml)}</span>{' '}
            <span style={{ color: 'var(--text-secondary)' }}>bez XML</span>
          </span>
        )}

        <label
          data-testid="btn-wgraj-liste"
          title="Miesięczna „Lista paragonów” od Renomy (PDF albo tekst z pdftotext)"
          style={{
            marginLeft: 'auto', padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px',
            cursor: wgrywanie ? 'default' : 'pointer', whiteSpace: 'nowrap',
            border: '1px solid var(--primary)', background: 'var(--card-bg)', color: 'var(--primary)', opacity: wgrywanie ? 0.6 : 1,
          }}
        >
          {wgrywanie ? 'Wgrywam…' : 'Wgraj listę PDF'}
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.txt,application/pdf,text/plain"
            aria-label="Lista paragonów PDF"
            data-testid="input-lista-paragonow"
            disabled={wgrywanie}
            onChange={(e) => wgraj(e.target.files && e.target.files[0])}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      {wynik && (
        <div data-testid="wynik-listy-paragonow" style={{ marginTop: '6px', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--success-light)', color: 'var(--success)' }}>
          Wczytano {n(wynik.wczytano)} pozycji z „{wynik.plik_nazwa}” (nowych {n(wynik.nowe)}, zaktualizowanych {n(wynik.zaktualizowane)})
          {wynik.suma_brutto !== undefined && wynik.suma_brutto !== null && <> · suma brutto {formatujKwote(wynik.suma_brutto)}</>}
          {wynikMiesiace.map(([m, v]) => (
            <span key={m}> · {formatujMiesiac(m)}: na liście {n(v?.na_liscie)}, w systemie {n(v?.w_systemie)}, brak {n(v?.brak)}, z XML {n(v?.z_xml)}</span>
          ))}
          {Array.isArray(wynik.ostrzezenia) && wynik.ostrzezenia.length > 0 && (
            <div data-testid="paragony-ostrzezenia" style={{ color: 'var(--warning)', marginTop: '4px' }}>
              Ostrzeżenia: {wynik.ostrzezenia.join('; ')}
            </div>
          )}
        </div>
      )}
      {bladWgrania && (
        <div data-testid="blad-listy-paragonow" style={{ marginTop: '6px', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)' }}>
          {bladWgrania}
        </div>
      )}
    </div>
  );
};

export default PasekParagonow;
